import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { User } from '@/apiServices/types';

// Which tenant the user is currently working in. null = "All tenants" (sysadmin only).
// Standalone on purpose: axiosInstance reads it on every request, so it must not import
// other services (LoggedUserService -> apiServices -> axiosInstance would be a cycle).

// Persisted marker for "All tenants" — the empty string can never be a real tenant name.
const ALL_TENANTS = '';

export class ActiveTenantServiceClass {
  private static readonly storageKeyPrefix = 'cmms.activeTenant.';

  public activeTenant: Ref<string | null> = ref(null);
  public tenantOptions: Ref<string[]> = ref([]);
  public isSysadmin: Ref<boolean> = ref(false);
  // Authoritative active-tenant list for the sysadmin "view as" picker, fed by the layout after
  // it fetches tenantAPI.getAll() (this service can't fetch it itself — see the note above).
  public viewAsOptions: Ref<string[]> = ref([]);
  // Distinguishes "list not fetched yet / fetch failed" from "fetched and there is genuinely one".
  public viewAsLoaded: Ref<boolean> = ref(false);

  // CASL subject to check privileges against. Under "All tenants" the user's privileges
  // still come from their own role assignments, so the first assignment tenant is used;
  // with no assignments the subject is empty and every check fails closed.
  public subject: ComputedRef<string> = computed(
    () => this.activeTenant.value ?? this.tenantOptions.value[0] ?? '',
  );

  // Switchable organizations: every active one for a sysadmin, otherwise own role assignments.
  public tenantChoices: ComputedRef<string[]> = computed(() =>
    this.isSysadmin.value && this.viewAsOptions.value.length
      ? this.viewAsOptions.value
      : this.tenantOptions.value,
  );

  // One organization means no picker. Sysadmins keep it while their authoritative list is
  // outstanding or never arrived (the fetch only toasts on failure) — otherwise a single-assignment
  // sysadmin on "All" could never pin to an organization — and whenever pinned, since "All" is
  // unrestricted rather than the union of the list.
  public showPicker: ComputedRef<boolean> = computed(
    () =>
      this.tenantChoices.value.length > 1
      || (this.isSysadmin.value
        && (this.activeTenant.value !== null || !this.viewAsLoaded.value)),
  );

  private storageKey = ActiveTenantServiceClass.storageKeyPrefix;

  //-----------------------------------------------------------------------------------------------
  public initFromUser(user: User | null): void {
    this.viewAsOptions.value = [];
    this.viewAsLoaded.value = false;
    if (!user) {
      this.activeTenant.value = null;
      this.tenantOptions.value = [];
      this.isSysadmin.value = false;
      this.storageKey = ActiveTenantServiceClass.storageKeyPrefix;
      return;
    }

    const assignments = user.userRoleAssignments ?? [];
    this.tenantOptions.value = [...new Set(assignments.map((a) => a.tenant).filter(Boolean))];
    this.isSysadmin.value = assignments.some((a) =>
      a.role?.rolePrivileges?.some((p) => p.privilegeName === 'sysadmin' || p.privilegeName === '*'),
    );
    this.storageKey = ActiveTenantServiceClass.storageKeyPrefix + user.userID;

    const stored = this.readStoredTenant();
    if (stored === ALL_TENANTS && this.isSysadmin.value) {
      this.activeTenant.value = null;
    } else if (stored && (this.tenantOptions.value.includes(stored) || this.isSysadmin.value)) {
      // Sysadmins may "view as" any tenant, membership or not (the server exempts them from
      // the X-Tenant membership check), so their stored choice is honored as-is.
      this.activeTenant.value = stored;
    } else if (this.isSysadmin.value) {
      this.activeTenant.value = null; // "All tenants" default
    } else {
      this.activeTenant.value = this.tenantOptions.value[0] ?? null;
    }
  }

  //-----------------------------------------------------------------------------------------------
  // The stored view-as choice is honored verbatim at init (before this list exists), so it is
  // reconciled here once the authoritative list arrives: a tenant deactivated, renamed or deleted
  // since it was persisted falls back to "All tenants". Returns the dropped tenant name so the
  // caller can tell the user, or null when the current choice is still valid.
  public setViewAsOptions(tenantNames: string[]): string | null {
    this.viewAsOptions.value = tenantNames;
    this.viewAsLoaded.value = true;
    const current = this.activeTenant.value;
    if (!this.isSysadmin.value || current === null || tenantNames.includes(current)) {
      return null;
    }
    this.setActiveTenant(null);
    return current;
  }

  //-----------------------------------------------------------------------------------------------
  // Persists the choice; the caller reloads the page so every view refetches under the new scope.
  public setActiveTenant(tenant: string | null): void {
    this.activeTenant.value = tenant;
    // Blocked storage (private mode) just loses persistence — the in-memory value still applies.
    try {
      window.localStorage.setItem(this.storageKey, tenant ?? ALL_TENANTS);
    } catch (error) {
      console.error('ActiveTenantService: failed to persist tenant choice', error);
    }
  }

  //-----------------------------------------------------------------------------------------------
  private readStoredTenant(): string | null {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (error) {
      console.error('ActiveTenantService: failed to read persisted tenant choice', error);
      return null;
    }
  }
}

export const ActiveTenantService = new ActiveTenantServiceClass();
